/**
 * Tool-native charts for 2×2 / classification matrix sessions (Impact/Effort, Eisenhower, MoSCoW).
 * Used on the Session Results index cards — replaces generic score bars + "untiered" donut.
 */

import { useMemo } from 'react';
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from 'recharts';
import {
  type MatrixReportVariant,
  quadrantMetaByKey,
  resolveItemBucket,
  resolveItemAgreement,
  formatBucketLabel,
} from '../../../lib/sessionResultVisualizations';
import type { SessionRankingOutput } from '../../../types/prioritization/session';

interface MatrixToolResultsChartsProps {
  variant: MatrixReportVariant;
  rankedIds: string[];
  output: SessionRankingOutput | null | undefined;
  itemsMap: Map<string, { title: string }>;
}

export function MatrixToolResultsCharts({
  variant,
  rankedIds,
  output,
  itemsMap,
}: MatrixToolResultsChartsProps) {
  const gridOrder = useMemo(
    () =>
      variant === 'impact_effort'
        ? ['quick-wins', 'big-bets', 'fill-ins', 'avoid']
        : variant === 'eisenhower'
          ? ['ui', 'in', 'un', 'nn']
          : ['must', 'should', 'could', 'wont'],
    [variant],
  );

  const byBucket = useMemo(() => {
    const map: Record<string, string[]> = Object.fromEntries(gridOrder.map(k => [k, []]));
    map.unclassified = [];
    for (const id of rankedIds) {
      const bucket = resolveItemBucket(output, id) ?? 'unclassified';
      if (bucket in map) map[bucket].push(id);
      else map.unclassified.push(id);
    }
    return map;
  }, [rankedIds, output, gridOrder]);

  const donutData = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const id of rankedIds) {
      const bucket = resolveItemBucket(output, id) ?? 'unclassified';
      counts[bucket] = (counts[bucket] ?? 0) + 1;
    }
    return Object.entries(counts)
      .filter(([, n]) => n > 0)
      .map(([name, value]) => ({
        name: name === 'unclassified' ? 'Unclassified' : formatBucketLabel(variant, name),
        key: name,
        value,
      }));
  }, [rankedIds, output, variant]);

  const distributionTitle =
    variant === 'impact_effort'
      ? 'Quadrant distribution'
      : variant === 'eisenhower'
        ? 'Quadrant distribution'
        : 'Tier distribution';

  const matrixTitle =
    variant === 'impact_effort'
      ? 'Impact / effort matrix'
      : variant === 'eisenhower'
        ? 'Eisenhower matrix'
        : 'MoSCoW tiers';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-3">
          {matrixTitle}
        </p>
        <div className="grid grid-cols-2 gap-3">
          {gridOrder.map(key => {
            const meta = quadrantMetaByKey(variant, key)!;
            const ids = byBucket[key] ?? [];
            return (
              <div
                key={key}
                className={` border p-3 min-h-[100px] ${meta.bgClass} ${meta.borderClass}`}
              >
                <div className={`text-xs font-semibold uppercase tracking-wide ${meta.textClass}`}>
                  {meta.label}
                </div>
                {meta.subtitle && (
                  <div className="text-[11px] text-[var(--text-ter)] mb-2">{meta.subtitle}</div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {ids.length === 0 ? (
                    <span className="text-xs text-[var(--text-ter)] italic">—</span>
                  ) : (
                    ids.map(id => {
                      const agreement = resolveItemAgreement(output, id);
                      const title = itemsMap.get(id)?.title ?? id;
                      return (
                        <span
                          key={id}
                          title={agreement != null ? `${agreement}% agreement` : title}
                          className={`text-xs px-2 py-0.5 rounded-full border ${meta.borderClass} ${meta.textClass} bg-[var(--bg-raised)]/80`}
                        >
                          {title.length > 32 ? `${title.slice(0, 30)}…` : title}
                          {agreement != null && (
                            <span className="opacity-60 ml-1">({agreement}%)</span>
                          )}
                        </span>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {(byBucket.unclassified?.length ?? 0) > 0 && (
          <p className="text-xs text-[var(--mustard)] mt-3">
            {byBucket.unclassified.length} item(s) could not be placed in a quadrant.
          </p>
        )}
      </div>

      {donutData.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-3">
            {distributionTitle}
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={donutData}
                cx="50%"
                cy="45%"
                innerRadius={55}
                outerRadius={80}
                dataKey="value"
                paddingAngle={2}
              >
                {donutData.map((entry) => (
                  <Cell
                    key={entry.key}
                    fill={
                      entry.key === 'unclassified'
                        ? '#cbd5e1'
                        : (quadrantMetaByKey(variant, entry.key)?.color ?? '#6366f1')
                    }
                  />
                ))}
              </Pie>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
